import { modelOptions, mongoose, PropType, post, prop } from "@typegoose/typegoose";
import { Milliseconds } from "../../shared.js";
import { GetTypeAtPath, PathInMongoUpdate } from "../../utils/mongoUpdateTypeFromModel.js";
import { DeviceVersion } from "../commonSubSchemas/deviceVersion.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { Gen4SensorSubsystemStates } from "./subSchemas/gen4SensorSubsystemStates.js";
import { Gen4ValveSubsystemsStates } from "./subSchemas/gen4ValveSubsystemsStates.js";
import { LORA_MODE } from "../loraNetworkProfile/subSchemas/responsivenessSettings.js";

const SCHEMA_KEY = "Gen4InternalStateTracker" as const;

@modelOptions({
    schemaOptions: {
        collection: "gen4internalstatetrackers",
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<Gen4InternalStateTracker>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Gen4InternalStateTracker extends SchemaBase<Gen4InternalStateTracker> {
    static readonly schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    public _id!: mongoose.Types.ObjectId;

    @prop({ type: () => String, required: true, unique: true })
    public node_id!: string;

    @prop({ type: () => Number, required: false })
    public idempOfLastUpdate?: number;

    @prop({ type: () => DeviceVersion, required: false })
    public version?: DeviceVersion;

    @prop({ type: () => Number, required: false })
    public standardUplinkPeriod?: Milliseconds;

    @prop({ type: () => Number, required: false })
    public resetCount?: number;

    @prop({ type: () => Number, required: false })
    public crashCount?: number;

    @prop({ type: () => Number, required: false })
    public lastErrorCode?: number;

    @prop({ type: () => String, required: false, enum: LORA_MODE })
    public loraMode?: LORA_MODE;

    @prop({ type: () => Number, required: false })
    public pingSlotPeriodicity?: number;

    @prop({ type: () => Gen4ValveSubsystemsStates, required: true, default: {} })
    valveSubsystems: Partial<Gen4ValveSubsystemsStates> = {};

    @prop({ type: () => Gen4SensorSubsystemStates, required: true, default: {} })
    sensorSubsystemStates: Partial<Gen4SensorSubsystemStates> = {};

    /**
     * If a ping pong uplink is received, then we must respond with whatever
     * we received. This tracks the body of the last ping pong uplink.
     */
    @prop({ type: () => Number, required: true, default: [] }, PropType.ARRAY)
    unresolvedPingPongBody: number[] = [];

    /**
     * If a ping pong uplink is received, then we must respond with whatever
     * we received. This tracks if we have a ping pong downlink that needs sending
     */
    @prop({ type: () => Boolean, required: true, default: false })
    unresolvedPingPongUplinkFlag: boolean = false;

    static getters = {} as const satisfies Record<string, (self: Gen4InternalStateTracker) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<Gen4InternalStateTracker>) {
        super(properties);
        Object.assign(this, properties);
    }
}

// some helper types to help us omit the schemaKey from all submodels, since it is not relevant to the
// state update operations (it isn't stored in the DB)
type OmitDistributive<T, K extends PropertyKey> = T extends any
    ? T extends object
        ? Id<OmitRecursively<T, K>>
        : T
    : never;
type Id<T> = NonNullable<unknown> & { [P in keyof T]: T[P] }; // Cosmetic use only makes the tooltips expand the type can be removed
type OmitRecursively<T, K extends PropertyKey> = Omit<{ [P in keyof T]: OmitDistributive<T[P], K> }, K>;

// Remove _id (which we never deal with) and schemaKey (which isn't in the DB) from the internal state tracker
// type that we use for doing state updates (where both properties are unnecessary
type Gen4InternalStateTrackerNoClutter = Omit<OmitRecursively<Gen4InternalStateTracker, "schemaKey">, "_id">;

// Complete Gen4InternalStateUpdate type using advanced MongoDB update typing
export type Gen4InternalStateUpdate = {
    [P in PathInMongoUpdate<Gen4InternalStateTrackerNoClutter>]?: GetTypeAtPath<Gen4InternalStateTrackerNoClutter, P>;
};

// Test examples to verify the type works correctly. If you have errors below, the TS magic above has broken.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const testUpdate: Gen4InternalStateUpdate[] = [
    {
        standardUplinkPeriod: 1,
    },
    {
        "version.hardwareVariant": "a",
    },
    {
        "valveSubsystems.0.valveOperationMode": "MANUAL_MODE" as any,
    },
    {
        "sensorSubsystemStates.2": {
            dataID: 1,
            sensingPeriod: 1,
            recordingInterval: 1,
            rawUpperAlarmThreshold: 0,
            rawLowerAlarmThreshold: 0,
        },
    },
    {
        sensorSubsystemStates: {},
    },
];
